/**
 * 🔄 Build Version Check
 * Detecta quando há uma nova versão do build e força reload
 * Previne erros de "Failed to fetch dynamically imported module"
 * 
 * ✅ AUDIT FIX: Usa storage.ts ao invés de localStorage direto
 * ✅ PREVIEW FIX: Pula em hosts de preview para evitar loops
 * ✅ AUDIT FIX: Migrado console.* para logger.*
 * ✅ AUDIT FIX: Usa __BUILD_TIME__ injetado pelo Vite (estável por build)
 *              ao invés de Date.now() (volátil por módulo import).
 *              Isso previne reloads na primeira visita após deploy.
 */

import { storage } from '@/lib/storage';
import { isPreviewHost } from '@/lib/previewDetection';
import { logger } from '@/lib/logger';

const BUILD_VERSION_KEY = 'auli_build_version';

// Injected by Vite at build time — stable across tabs/reloads for the same build
// Falls back to a fixed string in dev mode (no reload in dev)
declare const __BUILD_TIME__: number;
const CURRENT_BUILD_VERSION = (typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 0).toString();

export const checkAndUpdateBuildVersion = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  
  if (!import.meta.env.PROD) return;
  
  if (isPreviewHost()) {
    logger.debug('Preview host detectado: pulando build version check');
    return;
  }

  // If build time is 0 (dev/unknown), skip
  if (CURRENT_BUILD_VERSION === '0') return;
  
  const storedVersion = storage.get<string | null>(BUILD_VERSION_KEY, null);
  
  if (!storedVersion) {
    storage.set(BUILD_VERSION_KEY, CURRENT_BUILD_VERSION);
    return;
  }

  // Skip if same build (tabs share the same __BUILD_TIME__)
  if (storedVersion === CURRENT_BUILD_VERSION) return;
  
  const hoursSinceLastBuild = (Date.now() - parseInt(storedVersion)) / (1000 * 60 * 60);
  
  // 2h window — avoids reloading during long sessions (60min aulas)
  if (hoursSinceLastBuild > 2) {
    logger.info('Nova versão detectada, limpando cache...');
    
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        logger.debug('Caches do SW limpos', { count: cacheNames.length });
      }
      
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
        if (registrations.length > 0) {
          logger.debug(`Service Workers desregistrados: ${registrations.length}`);
        }
      }
    } catch (error) {
      logger.warn('Erro ao limpar caches/SW:', { error: String(error) });
    }
    
    storage.clearOldData();
    storage.set(BUILD_VERSION_KEY, CURRENT_BUILD_VERSION);
    window.location.reload();
  }
};
